/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {error} from '@ion/core/src/logger/logger';
import {
  createRoute,
  destroyRoute,
  loadRoutes,
  updateRoute
} from '@ion/core/src/services/Connect';
import {
  Entity,
  Route
} from '@ion/core/src/types/entities';
import {LoadRoutesParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageInitializeAction} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {getAccountId} from '../helpers';
import {toggleUserPrompt} from './user-prompt';

// name
export const NAME                         = 'adminRoutes';

// initialize routes ...
export const INITIALIZE_ADMIN_ROUTES      = 'INITIALIZE_ADMIN_ROUTES';
export const RE_INITIALIZE_ADMIN_ROUTES   = 'RE_INITIALIZE_ADMIN_ROUTES';

// refresh grid ...
export const ADMIN_ROUTES_GRID_REFRESHED  = 'ADMIN_ROUTES_GRID_REFRESHED';

// grid column state
export const SET_GRID_COLUMNS             = 'SET_ADMIN_ROUTES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS          = 'UPDATE_ADMIN_ROUTES_GRID_COLUMNS';

// retrieve routes ...
export const GET_ADMIN_ROUTES_REQUEST     = 'GET_ADMIN_ROUTES_REQUEST';
export const GET_ADMIN_ROUTES_RESPONSE    = 'GET_ADMIN_ROUTES_RESPONSE';
export const GET_ADMIN_ROUTES_ERROR       = 'GET_ADMIN_ROUTES_ERROR';

// route add ...
export const ADD_ADMIN_ROUTE              = 'ADD_ADMIN_ROUTE';
export const ADD_ADMIN_ROUTE_REQUEST      = 'ADD_ADMIN_ROUTE_REQUEST';
export const ADD_ADMIN_ROUTE_RESPONSE     = 'ADD_ADMIN_ROUTE_RESPONSE';
export const ADD_ADMIN_ROUTE_ERROR        = 'ADD_ADMIN_ROUTE_ERROR';

// route edit ...
export const EDIT_ADMIN_ROUTE             = 'EDIT_ADMIN_ROUTE';
export const EDIT_ADMIN_ROUTE_REQUEST     = 'EDIT_ADMIN_ROUTE_REQUEST';
export const EDIT_ADMIN_ROUTE_RESPONSE    = 'EDIT_ADMIN_ROUTE_RESPONSE';
export const EDIT_ADMIN_ROUTE_ERROR       = 'EDIT_ADMIN_ROUTE_ERROR';

// route delete ...
export const DELETE_ADMIN_ROUTE           = 'DELETE_ADMIN_ROUTE';
export const DELETE_ADMIN_ROUTE_REQUEST   = 'DELETE_ADMIN_ROUTE_REQUEST';
export const DELETE_ADMIN_ROUTE_RESPONSE  = 'DELETE_ADMIN_ROUTE_RESPONSE';
export const DELETE_ADMIN_ROUTE_ERROR     = 'DELETE_ADMIN_ROUTE_ERROR';

// admin routes filter model ...
export const SET_ADMIN_ROUTES_GRID_FILTER_MODEL = 'SET_ADMIN_ROUTES_GRID_FILTER_MODEL';

/**
 * @initialize
 */
export const initializeAdminRoutes = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_ROUTES};
};

export const reInitializeAdminRoutes = (): PageInitializeAction => {
  return {type: RE_INITIALIZE_ADMIN_ROUTES};
};

/**
 * @adminRoutesGridRefreshed
 */
export const adminRoutesGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_ROUTES_GRID_REFRESHED};
};

/**
 * @getAdminRoutes
 *
 * fetches routes for this account to be displayed in the admin
 * section
 */
export const getAdminRoutes = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_ROUTES_REQUEST});
    const data: LoadRoutesParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const {routes: items, totalCount} = await loadRoutes(data);
      dispatch({type: GET_ADMIN_ROUTES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_ROUTES_ERROR, error: err});
      return error(err);
    }
  };
};

/**
 * @addAdminRoute
 *
 * Invoked when the Add Admin Route editor is to be displayed or removed.
 */
export const addAdminRoute = (show = true) => ({
  type : ADD_ADMIN_ROUTE, show
});

/**
 * @addRoute
 *
 * Invoked when a new route is to be created, delegating to the createRoute
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addRoute = (route: Route) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_ROUTE_REQUEST});
    try {
      const created = await createRoute(route);
      dispatch({type: ADD_ADMIN_ROUTE_RESPONSE, route: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.route)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_ROUTE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.route)));
      error(err);
    }
  };
};

/**
 * @editAdminRoute
 *
 * Invoked when the Edit Admin Route editor is to be displayed or removed.
 */
export const editAdminRoute = (route: Route, show = true) => ({
  type : EDIT_ADMIN_ROUTE, route, show
});

/**
 * @editRoute
 *
 * Invoked when an existing route is to be updated, delegating to the updateRoute
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editRoute = (route: Route) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_ROUTE_REQUEST});
    try {
      const updated = await updateRoute(route);
      dispatch({type: EDIT_ADMIN_ROUTE_RESPONSE, route: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.route)));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_ROUTE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.route)));
      error(err);
    }
  };
};

/**
 * Invoked when the Delete Admin Route Dialog is to be displayed or removed.
 */
export const deleteAdminRoute = (route: Route, show = true) => ({
  type : DELETE_ADMIN_ROUTE, route, show
});

/**
 * Invoked when an existing route is to be deleted, delegating to the deleteRoute
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteRoute = (route: Route) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_ROUTE_REQUEST});

    try {
      const deleted = await destroyRoute(route);
      dispatch({type: DELETE_ADMIN_ROUTE_RESPONSE, route: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_ROUTE_ERROR, error: err});
      error(err);
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_ROUTES_GRID_FILTER_MODEL, model
});
